import {
  Analytics as AnalyticsIcon,
  Category as CategoryIcon,
  Clear as ClearIcon,
  Inventory as InventoryIcon,
  KeyboardArrowDown as ArrowDownIcon,
  ShoppingBasket as BasketIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Collapse,
  Container,
  Divider,
  GridLegacy as Grid,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import MuiAutocompleteMultiSelect from "@/components/common/MuiAutocompleteMultiSelect";
import { AdminFilterPanel } from "@/components/common/AdminFilterPanel";
import { PageHeader } from "@/components/common/PageHeader";
import { unifiedTableSx } from "@/components/common/unifiedTableSx";
import { useAuth } from "@/contexts/AuthContext";
import { paths } from "@/routes/paths";
import { GroupedInventoryItem, InventoryService } from "@/services";
import { CoreService } from "@/services/core/CoreService";
import { Customer, Store, StoreService } from "@/services/features/StoreService";
import { usePermission } from "@/utils/permissionManager";
import { formatStoreSelectorLabel } from "@/utils/store";

const Inventory: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { isCraveUser } = usePermission(user?.role || "");
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  // Services
  const storeService = useMemo(() => StoreService.getInstance(), []);
  const coreService = useMemo(() => CoreService.getInstance(), []);
  const inventoryService = useMemo(
    () => new InventoryService(coreService.getAuthorizationHeader() || ""),
    [coreService],
  );
  // Add this near the top of the component
  const PRELOAD_THRESHOLD = 0.2; // Number of pages to load ahead

  // State management
  const [state, setState] = useState({
    loading: {
      isLoadingInitial: true,
      isLoadingStores: false,
      isLoadingInventory: false,
    },
    error: null as string | null,
    data: {
      stores: [] as Store[],
      customers: [] as Customer[],
      allStores: [] as Store[],
      inventoryMap: new Map<string, GroupedInventoryItem>(),
      displayInventory: [] as GroupedInventoryItem[],
      filteredInventory: [] as GroupedInventoryItem[],
      pendingInventory: [] as GroupedInventoryItem[],
      stats: {
        totalItems: 0,
        totalVariants: 0,
        totalQuantity: 0,
        uniqueProducts: 0,
      },
    },
    filters: {
      searchQuery: "",
    },
    pagination: {
      currentPage: 1,
      hasMore: true,
      isPreloading: false,
    },
    selection: {
      selectedCustomer: null as Customer | null,
      selectedStore: null as Store | null,
    },
    expandedGroups: new Set<string>(),
  });

  // Update state helper
  const updateState = useCallback((updater: (prevState: typeof state) => typeof state) => {
    setState((prev) => updater(prev));
  }, []);

  // Ensure only Crave users can access this page
  useEffect(() => {
    if (!isCraveUser()) {
      window.location.href = paths.storeInsights.fittingRoomsLiveView;
    }
  }, [isCraveUser]);

  // Fetch initial data (customers and all stores)
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        updateState((prev) => ({
          ...prev,
          loading: { ...prev.loading, isLoadingInitial: true },
        }));

        // Get customers first
        const customersResponse = await storeService.getCustomers();

        if (customersResponse.data) {
          // Check if we have a customer ID in the URL
          const customerIdParam = searchParams.get("customerId");
          const storeIdParam = searchParams.get("storeId");

          if (customerIdParam) {
            const customer = customersResponse.data.find((c) => c.id === parseInt(customerIdParam));
            if (customer) {
              // Fetch stores for this customer
              const storesResponse = await storeService.getStoresByCustomer(customer.id);

              // Look for the selected store if we have a storeId
              let selectedStore: Store | null = null;
              if (storeIdParam && storesResponse.data) {
                const storeId = parseInt(storeIdParam);
                selectedStore = storesResponse.data.find((s) => s.id === storeId) || null;

                if (!selectedStore) {
                }
              }

              // Set the selected customer and store
              updateState((prev) => ({
                ...prev,
                selection: {
                  selectedCustomer: customer,
                  selectedStore: selectedStore,
                },
                data: {
                  ...prev.data,
                  customers: customersResponse.data || [],
                  stores: storesResponse.data || [],
                },
                loading: {
                  ...prev.loading,
                  isLoadingInitial: false,
                  isLoadingStores: false,
                },
              }));
            } else {
              // If customer not found, just load the customers list
              updateState((prev) => ({
                ...prev,
                loading: {
                  ...prev.loading,
                  isLoadingInitial: false,
                  isLoadingStores: false,
                },
                data: {
                  ...prev.data,
                  customers: customersResponse.data || [],
                  stores: [], // Clear stores when no customer is selected
                },
              }));
            }
          } else {
            // If no customer ID in URL, just set customers
            updateState((prev) => ({
              ...prev,
              loading: {
                ...prev.loading,
                isLoadingInitial: false,
                isLoadingStores: false,
              },
              data: {
                ...prev.data,
                customers: customersResponse.data || [],
                stores: [], // Clear stores when no customer is selected
              },
            }));
          }
        }
      } catch (error) {
        updateState((prev) => ({
          ...prev,
          loading: {
            ...prev.loading,
            isLoadingInitial: false,
            isLoadingStores: false,
          },
          error:
            error instanceof Error ? error.message : t("inventory.errors.failedFetchInitialData"),
        }));
      }
    };

    fetchInitialData();
  }, [updateState, storeService, searchParams]);

  // Handle URL parameters and state
  useEffect(() => {
    const handleInitialSelection = async () => {
      const customerIdParam = searchParams.get("customerId");
      const storeIdParam = searchParams.get("storeId");
      const { selectedCustomer, selectedStore } = location.state || {};

      // Skip if we're still loading initial data
      if (state.loading.isLoadingInitial) return;

      // Skip if both customer and store are already selected (likely set by fetchInitialData)
      if (
        state.selection.selectedCustomer &&
        state.selection.selectedStore &&
        state.selection.selectedCustomer.id.toString() === customerIdParam &&
        state.selection.selectedStore.id.toString() === storeIdParam
      ) {
        return;
      }

      try {
        // Case 1: URL parameters take precedence
        if (customerIdParam) {
          // First find the customer by ID
          const customer = state.data.customers.find((c) => c.id === parseInt(customerIdParam));
          if (customer) {
            // Update the selected customer
            updateState((prev) => ({
              ...prev,
              loading: { ...prev.loading, isLoadingStores: true },
              selection: {
                ...prev.selection,
                selectedCustomer: customer,
              },
            }));

            // Fetch and update stores for this customer
            const storesResponse = await storeService.getStoresByCustomer(customer.id);
            if (storesResponse.success) {
              const filteredStores = storesResponse.data || [];

              // If we also have a store ID, find it in the filtered stores
              let selectedStore: Store | null = null;
              if (storeIdParam) {
                const storeId = parseInt(storeIdParam);

                selectedStore = filteredStores.find((s) => s.id === storeId) || null;

                // Diagnostic: List all store IDs to check if our target ID exists
              }

              // Update stores and selected store
              updateState((prev) => ({
                ...prev,
                loading: { ...prev.loading, isLoadingStores: false },
                data: {
                  ...prev.data,
                  stores: filteredStores,
                },
                selection: {
                  ...prev.selection,
                  selectedStore,
                },
              }));
            } else {
            }
          } else {
          }
        }
        // Case 2: Handle state from navigation if URL params are not present
        else if (selectedCustomer) {
          updateState((prev) => ({
            ...prev,
            loading: { ...prev.loading, isLoadingStores: true },
            selection: {
              selectedCustomer,
              selectedStore: selectedStore || null,
            },
          }));

          const storesResponse = await storeService.getStoresByCustomer(selectedCustomer.id);
          if (storesResponse.success) {
            updateState((prev) => ({
              ...prev,
              loading: { ...prev.loading, isLoadingStores: false },
              data: {
                ...prev.data,
                stores: storesResponse.data || [],
              },
            }));
          }
        }
        // Case 3: Only storeId in URL without customerId
        else if (storeIdParam && !customerIdParam) {
          try {
            // Load all stores to find the matching one
            const storesResponse = await storeService.getStores();
            if (storesResponse.success) {
              const allStores = storesResponse.data || [];
              const storeId = parseInt(storeIdParam);
              const matchingStore = allStores.find((s) => s.id === storeId);

              if (matchingStore) {
                // Find the customer for this store
                const customerResponse = await storeService.getCustomers();
                if (customerResponse.success && customerResponse.data) {
                  // Find which customer this store belongs to
                  for (const customer of customerResponse.data) {
                    const customerStores = await storeService.getStoresByCustomer(customer.id);
                    if (customerStores.success && customerStores.data) {
                      const storeExists = customerStores.data.some(
                        (s) => s.id === matchingStore.id,
                      );
                      if (storeExists) {
                        // Found the customer that owns this store

                        updateState((prev) => ({
                          ...prev,
                          loading: { ...prev.loading, isLoadingStores: false },
                          data: {
                            ...prev.data,
                            stores: customerStores.data,
                            allStores: allStores,
                          },
                          selection: {
                            selectedCustomer: customer,
                            selectedStore: matchingStore,
                          },
                        }));
                        break;
                      }
                    }
                  }
                }
              }
            }
          } catch {}
        }
      } catch {
        updateState((prev) => ({
          ...prev,
          loading: { ...prev.loading, isLoadingStores: false },
          error: t("inventory.errors.failedLoadStoreData"),
        }));
      }
    };

    handleInitialSelection();
  }, [
    location.state,
    searchParams,
    state.loading.isLoadingInitial,
    state.data.customers,
    storeService,
    updateState,
  ]);

  // Update URL when selection changes
  useEffect(() => {
    const params = new URLSearchParams();
    if (state.selection.selectedCustomer) {
      params.set("customerId", state.selection.selectedCustomer.id.toString());
    }
    if (state.selection.selectedStore) {
      params.set("storeId", state.selection.selectedStore.id.toString());
    }
    setSearchParams(params);
  }, [state.selection.selectedCustomer, state.selection.selectedStore]);
  const handleCustomerSelect = useCallback(
    async (customer: Customer | null) => {
      // Check if there's a storeId in the URL parameters
      const storeIdParam = searchParams.get("storeId");

      // First, update the customer selection immediately
      updateState((prev) => ({
        ...prev,
        loading: { ...prev.loading, isLoadingStores: true },
        selection: {
          ...prev.selection,
          selectedCustomer: customer,
          // Don't reset store yet - we'll determine the correct store after loading store data
        },
      }));

      if (customer) {
        try {
          // Load stores for the selected customer
          const response = await storeService.getStoresByCustomer(customer.id);

          if (response.success) {
            // Get the filtered stores
            const filteredStores = response.data || [];

            // If there's a storeId in the URL, try to find that store
            let selectedStore: Store | null = null;

            if (storeIdParam) {
              const storeId = parseInt(storeIdParam);
              const matchingStore = filteredStores.find((s) => s.id === storeId);

              if (matchingStore) {
                selectedStore = matchingStore;
              } else {
              }
            }

            // Update the stores and selected store
            updateState((prev) => ({
              ...prev,
              loading: { ...prev.loading, isLoadingStores: false },
              data: {
                ...prev.data,
                stores: filteredStores,
              },
              selection: {
                ...prev.selection,
                selectedStore,
              },
            }));
          } else {
            // Handle unsuccessful response

            updateState((prev) => ({
              ...prev,
              loading: { ...prev.loading, isLoadingStores: false },
              error: t("inventory.errors.failedLoadStores"),
              data: {
                ...prev.data,
                stores: [],
              },
              selection: {
                ...prev.selection,
                selectedStore: null,
              },
            }));
          }
        } catch {
          updateState((prev) => ({
            ...prev,
            loading: { ...prev.loading, isLoadingStores: false },
            error: t("inventory.errors.failedLoadStores"),
            data: {
              ...prev.data,
              stores: [],
            },
            selection: {
              ...prev.selection,
              selectedStore: null,
            },
          }));
        }
      } else {
        // If no customer is selected, clear the stores immediately
        // But still respect storeId from URL if it exists
        if (storeIdParam) {
          try {
            // If there's a storeId but no customer, try to load all stores and select the one from the URL
            const response = await storeService.getStores();
            if (response.success) {
              const allStores = response.data || [];
              const storeId = parseInt(storeIdParam);
              const matchingStore = allStores.find((s) => s.id === storeId);

              updateState((prev) => ({
                ...prev,
                loading: { ...prev.loading, isLoadingStores: false },
                data: {
                  ...prev.data,
                  stores: allStores,
                  allStores: allStores,
                },
                selection: {
                  ...prev.selection,
                  selectedStore: matchingStore || null,
                },
              }));
              return;
            }
          } catch {}
        }

        // Default case: clear stores and selection
        updateState((prev) => ({
          ...prev,
          loading: { ...prev.loading, isLoadingStores: false },
          data: {
            ...prev.data,
            stores: [],
          },
          selection: {
            ...prev.selection,
            selectedStore: null,
          },
        }));
      }
    },
    [storeService, updateState, searchParams],
  );

  const handleStoreSearch = useCallback(
    (event: React.SyntheticEvent | null, store: Store | null) => {
      updateState((prev) => ({
        ...prev,
        selection: {
          ...prev.selection,
          selectedStore: store,
        },
        loading: {
          ...prev.loading,
          isLoadingStores: false, // Ensure loading state is cleared when store is selected
        },
      }));
    },
    [updateState],
  );

  // Add this handler for expand/collapse
  const handleToggleExpand = useCallback(
    (productId: string) => {
      updateState((prev) => {
        const newExpandedGroups = new Set(prev.expandedGroups);
        if (newExpandedGroups.has(productId)) {
          newExpandedGroups.delete(productId);
        } else {
          newExpandedGroups.add(productId);
        }
        return {
          ...prev,
          expandedGroups: newExpandedGroups,
        };
      });
    },
    [updateState],
  );

  // Create a memoized variant table component
  const VariantTable = React.memo(
    ({ variants }: { variants: GroupedInventoryItem["variants"] }) => {
      const hasSize2 = variants.some((v) => v.size2);

      return (
        <TableContainer
          component={Paper}
          elevation={1}
          sx={{
            maxHeight: 400,
            overflowY: "auto",
            "& .MuiTableCell-root": {
              px: 2,
              py: 1.5,
              borderBottom: "1px solid",
              borderColor: "divider",
              whiteSpace: "nowrap",
            },
            "& .MuiTableCell-head": {
              backgroundColor: (theme) => theme.palette.grey[50],
              position: "sticky",
              top: 0,
              zIndex: 1,
              fontWeight: 600,
            },
          }}
        >
          <Table size="small" sx={{ ...unifiedTableSx, tableLayout: "fixed" }}>
            <TableHead>
              <TableRow>
                <TableCell width="15%">{t("inventory.columns.sku")}</TableCell>
                <TableCell width="20%">{t("inventory.columns.color")}</TableCell>
                <TableCell width="15%">{t("inventory.columns.size")}</TableCell>
                {hasSize2 && (
                  <TableCell width="15%">
                    {variants[0].size2Label || t("inventory.columns.size2")}
                  </TableCell>
                )}
                <TableCell width="15%" align="right">
                  {t("inventory.columns.quantity")}
                </TableCell>
                <TableCell width={hasSize2 ? "20%" : "35%"} align="right">
                  {t("inventory.columns.status")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {variants.map((variant, index) => (
                <TableRow
                  key={`${variant.skuId}-${index}`}
                  sx={{
                    "&:last-child td": { borderBottom: 0 },
                    "&:hover": {
                      backgroundColor: "action.hover",
                    },
                  }}
                >
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: "monospace",
                        fontWeight: 500,
                        color: "text.secondary",
                      }}
                    >
                      {variant.skuId}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box
                        sx={{
                          width: 16,
                          height: 16,
                          borderRadius: "50%",
                          bgcolor: variant.color.toLowerCase(),
                          border: "1px solid",
                          borderColor: "divider",
                        }}
                      />
                      <Typography variant="body2">{variant.color}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{variant.size}</Typography>
                  </TableCell>
                  {hasSize2 && (
                    <TableCell>
                      <Typography variant="body2">{variant.size2 || "-"}</Typography>
                    </TableCell>
                  )}
                  <TableCell align="right">
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {variant.quantity}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Chip
                      label={
                        variant.quantity > 0 ? t("inventory.inStock") : t("inventory.outOfStock")
                      }
                      size="small"
                      color={variant.quantity > 0 ? "success" : "error"}
                      variant="outlined"
                      sx={{
                        height: 24,
                        fontSize: "0.75rem",
                        fontWeight: 500,
                        "& .MuiChip-label": { px: 1 },
                      }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      );
    },
  );
  VariantTable.displayName = "VariantTable";

  // Add navigation handler
  const handleProductClick = useCallback(
    (group: GroupedInventoryItem) => {
      if (!state.selection.selectedStore || !group.variants.length) return;

      // Get the first SKU ID from the variants
      const firstSkuId = group.variants[0].skuId;

      navigate(`/admin/experimental/inventory/product/${firstSkuId}`, {
        state: {
          storeId: state.selection.selectedStore.id,
          productId: group.productId,
          selectedCustomer: state.selection.selectedCustomer,
          selectedStore: state.selection.selectedStore,
        },
      });
    },
    [navigate, state.selection.selectedStore, state.selection.selectedCustomer],
  );

  // Fix the loadInventoryPage function to handle the response correctly
  const loadInventoryPage = useCallback(
    async (storeId: number, page: number) => {
      try {
        const response = await inventoryService.loadInventoryPage(storeId, page);
        return {
          items: response.items || [],
          hasMore: response.items && response.items.length > 0,
        };
      } catch (error) {
        throw error;
      }
    },
    [inventoryService],
  );

  // Fix the updateInventoryWithNewItems function to maintain store selection
  const updateInventoryWithNewItems = useCallback(
    (items: GroupedInventoryItem[]) => {
      updateState((prev) => {
        // Create a new map with the items
        const newInventoryMap = new Map<string, GroupedInventoryItem>();

        // Process all items
        items.forEach((item) => {
          newInventoryMap.set(item.productId, item);
        });

        // Convert map to array and sort by title
        const displayInventory = Array.from(newInventoryMap.values()).sort((a, b) =>
          a.title.localeCompare(b.title),
        );

        // Calculate stats
        const stats = {
          totalItems: displayInventory.length,
          totalVariants: displayInventory.reduce((sum, item) => sum + item.variants.length, 0),
          totalQuantity: displayInventory.reduce((sum, item) => sum + item.totalQuantity, 0),
          uniqueProducts: displayInventory.length,
        };

        // Maintain the existing selection while updating the inventory data
        return {
          ...prev,
          data: {
            ...prev.data,
            inventoryMap: newInventoryMap,
            displayInventory,
            filteredInventory: prev.filters.searchQuery
              ? displayInventory.filter(
                  (item) =>
                    item.title.toLowerCase().includes(prev.filters.searchQuery.toLowerCase()) ||
                    item.productId.toLowerCase().includes(prev.filters.searchQuery.toLowerCase()) ||
                    item.variants.some(
                      (v) =>
                        v.skuId.toLowerCase().includes(prev.filters.searchQuery.toLowerCase()) ||
                        v.color.toLowerCase().includes(prev.filters.searchQuery.toLowerCase()) ||
                        v.size.toLowerCase().includes(prev.filters.searchQuery.toLowerCase()) ||
                        (v.size2 &&
                          v.size2.toLowerCase().includes(prev.filters.searchQuery.toLowerCase())),
                    ),
                )
              : displayInventory,
            stats,
          },
        };
      });
    },
    [updateState],
  );

  // Reset inventory when store changes
  useEffect(() => {
    if (!state.selection.selectedStore) {
      updateState((prev) => ({
        ...prev,
        data: {
          ...prev.data,
          inventoryMap: new Map(),
          displayInventory: [],
          filteredInventory: [],
        },
        pagination: {
          currentPage: 1,
          hasMore: true,
          isPreloading: false,
        },
      }));
      return;
    }

    const loadAllInventory = async () => {
      try {
        // Set loading state without clearing the store selection
        updateState((prev) => ({
          ...prev,
          loading: { ...prev.loading, isLoadingInventory: true },
          error: null,
        }));

        const selectedStore = state.selection.selectedStore;
        if (!selectedStore) {
          throw new Error(t("inventory.errors.noStoreSelected"));
        }

        // Create an array of promises for all pages (up to 30)
        const pagePromises = Array.from({ length: 30 }, (_, i) =>
          loadInventoryPage(selectedStore.id, i + 1).catch((error) => {
            // If we get a 500 error, return empty array (end of data)
            if (error?.response?.status === 500) {
              return { items: [] };
            }
            throw error;
          }),
        );

        // Wait for all requests to complete
        const results = await Promise.all(pagePromises);

        // Combine all items, filtering out empty responses
        const allItems = results
          .filter((result) => result.items && result.items.length > 0)
          .flatMap((result) => result.items);

        // Update with all items at once
        if (allItems.length > 0) {
          updateInventoryWithNewItems(allItems);
        }

        // Update state to show we're done loading
        updateState((prev) => ({
          ...prev,
          loading: { ...prev.loading, isLoadingInventory: false },
          pagination: {
            currentPage: 1,
            hasMore: false,
            isPreloading: false,
          },
        }));
      } catch (error) {
        updateState((prev) => ({
          ...prev,
          loading: { ...prev.loading, isLoadingInventory: false },
          error:
            error instanceof Error ? error.message : t("inventory.errors.failedFetchInventory"),
        }));
      }
    };

    loadAllInventory();
  }, [state.selection.selectedStore, loadInventoryPage, updateInventoryWithNewItems, updateState]);

  // Fix the handleSearch function
  const handleSearch = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const searchQuery = event.target.value;
      updateState((prev) => ({
        ...prev,
        filters: {
          ...prev.filters,
          searchQuery,
        },
        data: {
          ...prev.data,
          filteredInventory: searchQuery
            ? prev.data.displayInventory.filter(
                (item) =>
                  item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                  item.productId.toLowerCase().includes(searchQuery.toLowerCase()) ||
                  item.variants.some(
                    (v) =>
                      v.skuId.toLowerCase().includes(searchQuery.toLowerCase()) ||
                      v.color.toLowerCase().includes(searchQuery.toLowerCase()) ||
                      v.size.toLowerCase().includes(searchQuery.toLowerCase()) ||
                      (v.size2 && v.size2.toLowerCase().includes(searchQuery.toLowerCase())),
                  ),
              )
            : prev.data.displayInventory,
        },
      }));
    },
    [updateState],
  );

  // Remove the infinite scroll effect since we're loading everything at once
  useEffect(() => {
    if (
      !state.loading.isLoadingInventory &&
      state.pagination.hasMore &&
      state.data.displayInventory.length > 0
    ) {
      const scrollPosition = window.innerHeight + window.pageYOffset;
      const totalHeight = document.documentElement.scrollHeight;
      const scrollPercentage = scrollPosition / totalHeight;

      if (scrollPercentage > PRELOAD_THRESHOLD) {
        updateState((prev) => ({
          ...prev,
          pagination: {
            ...prev.pagination,
            isPreloading: true,
          },
        }));
      }
    }
  }, [
    state.loading.isLoadingInventory,
    state.pagination.hasMore,
    state.data.displayInventory.length,
    updateState,
  ]);

  // Update stores when customer changes - modify this effect
  useEffect(() => {
    const updateStores = async () => {
      // Skip if we're still loading initial data
      if (state.loading.isLoadingInitial) return;

      if (state.selection.selectedCustomer) {
        try {
          updateState((prev) => ({
            ...prev,
            loading: { ...prev.loading, isLoadingStores: true },
          }));

          const response = await storeService.getStoresByCustomer(
            state.selection.selectedCustomer.id,
          );
          if (response.success) {
            updateState((prev) => ({
              ...prev,
              loading: { ...prev.loading, isLoadingStores: false },
              data: {
                ...prev.data,
                stores: response.data || [],
              },
            }));
          }
        } catch (error) {
          updateState((prev) => ({
            ...prev,
            loading: { ...prev.loading, isLoadingStores: false },
            error: error instanceof Error ? error.message : t("inventory.errors.failedFetchStores"),
          }));
        }
      } else {
        // Only show all stores if there's no customer ID in the URL
        const customerIdParam = searchParams.get("customerId");
        if (!customerIdParam) {
          // Only update to all stores if we actually have allStores loaded
          if (state.data.allStores.length > 0) {
            updateState((prev) => ({
              ...prev,
              data: {
                ...prev.data,
                stores: prev.data.allStores,
              },
            }));
          } else {
            // If we don't have allStores, fetch them first
            try {
              const response = await storeService.getStores();
              updateState((prev) => ({
                ...prev,
                data: {
                  ...prev.data,
                  allStores: response.data || [],
                  stores: response.data || [],
                },
              }));
            } catch {}
          }
        }
      }
    };

    updateStores();
  }, [
    state.selection.selectedCustomer,
    storeService,
    updateState,
    searchParams,
    state.loading.isLoadingInitial,
    state.data.allStores.length,
  ]);

  // Modify the clear filters function to handle URL parameters
  const handleClearFilters = useCallback(() => {
    updateState((prev) => ({
      ...prev,
      selection: {
        selectedCustomer: null,
        selectedStore: null,
      },
      loading: {
        ...prev.loading,
        isLoadingStores: false,
        isLoadingInventory: false,
      },
      data: {
        ...prev.data,
        stores: [],
        inventoryMap: new Map(),
        displayInventory: [],
        filteredInventory: [],
      },
      filters: {
        searchQuery: "",
      },
    }));
    // Clear URL parameters
    setSearchParams({});
  }, [updateState, setSearchParams]);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Modify the product list rendering to be mobile-responsive
  const renderProductItem = (group: GroupedInventoryItem) => {
    if (isMobile) {
      return (
        <Paper
          key={group.productId}
          elevation={0}
          sx={{
            border: 1,
            borderColor: "divider",
            "&:hover": {
              bgcolor: "action.hover",
              cursor: "pointer",
            },
          }}
          onClick={() => handleProductClick(group)}
        >
          <Box sx={{ p: 2 }}>
            {/* Product Header */}
            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
              {/* Thumbnail */}
              <Box sx={{ width: 60, height: 60, flexShrink: 0 }}>
                <img
                  src={group.imageUrl}
                  alt={group.title}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    borderRadius: 4,
                  }}
                />
              </Box>

              {/* Title and Basic Info */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle1" noWrap>
                  {group.title}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" noWrap>
                  {group.brand} • {group.category}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  {t("inventory.productIdLabel", { productId: group.productId })}
                </Typography>
              </Box>
            </Stack>

            {/* Stats and Price */}
            <Stack spacing={1}>
              <Stack direction="row" spacing={1}>
                <Chip
                  label={t("inventory.variantsCount", { count: group.variants.length })}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
                <Chip
                  label={t("inventory.stockCount", { count: group.totalQuantity })}
                  size="small"
                  color={group.totalQuantity > 0 ? "success" : "error"}
                  variant="outlined"
                />
              </Stack>

              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="subtitle2">
                  {group.price}
                  {group.salePrice && (
                    <Typography
                      component="span"
                      variant="caption"
                      color="error.main"
                      sx={{ ml: 1 }}
                    >
                      {group.salePrice}
                    </Typography>
                  )}
                </Typography>
                <Button
                  size="small"
                  onClick={(event) => {
                    event.stopPropagation();
                    handleToggleExpand(group.productId);
                  }}
                  endIcon={
                    <ArrowDownIcon
                      sx={{
                        transform: state.expandedGroups.has(group.productId)
                          ? "rotate(180deg)"
                          : "none",
                        transition: "transform 0.15s",
                      }}
                    />
                  }
                >
                  {state.expandedGroups.has(group.productId)
                    ? t("inventory.actions.hideDetails")
                    : t("inventory.actions.showDetails")}
                </Button>
              </Stack>
            </Stack>
          </Box>

          <Collapse in={state.expandedGroups.has(group.productId)}>
            <Divider />
            <Box sx={{ p: 2, bgcolor: (theme) => theme.palette.grey[50] }}>
              <MobileVariantTable variants={group.variants} />
            </Box>
          </Collapse>
        </Paper>
      );
    }

    // Desktop view remains unchanged
    return (
      <Paper
        key={group.productId}
        elevation={0}
        sx={{
          border: 1,
          borderColor: "divider",
          "&:hover": {
            bgcolor: "action.hover",
            cursor: "pointer",
          },
        }}
        onClick={() => handleProductClick(group)}
      >
        <Box sx={{ display: "flex", alignItems: "center", p: 2 }}>
          {/* Existing desktop layout */}
          <Box sx={{ width: 60, height: 60, flexShrink: 0, mr: 2 }}>
            <img
              src={group.imageUrl}
              alt={group.title}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                borderRadius: 4,
              }}
            />
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle1" noWrap>
                  {group.title}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" noWrap>
                  {t("inventory.productSummary", {
                    brand: group.brand,
                    category: group.category,
                    productId: group.productId,
                  })}
                </Typography>
              </Box>
              <Stack direction="row" spacing={2} alignItems="center" sx={{ flexShrink: 0 }}>
                <Chip
                  label={t("inventory.variantsCount", { count: group.variants.length })}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
                <Chip
                  label={t("inventory.stockCount", { count: group.totalQuantity })}
                  size="small"
                  color={group.totalQuantity > 0 ? "success" : "error"}
                  variant="outlined"
                />
                <Typography variant="subtitle2" sx={{ minWidth: 100, textAlign: "end" }}>
                  {group.price}
                  {group.salePrice && (
                    <Typography
                      component="span"
                      variant="caption"
                      color="error.main"
                      sx={{ ml: 1 }}
                    >
                      {group.salePrice}
                    </Typography>
                  )}
                </Typography>
                <Button
                  onClick={(event) => {
                    event.stopPropagation();
                    handleToggleExpand(group.productId);
                  }}
                  sx={{ minWidth: 40 }}
                >
                  <ArrowDownIcon
                    sx={{
                      transform: state.expandedGroups.has(group.productId)
                        ? "rotate(180deg)"
                        : "none",
                      transition: "transform 0.15s",
                    }}
                  />
                </Button>
              </Stack>
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            height: state.expandedGroups.has(group.productId) ? "auto" : 0,
            overflow: "hidden",
            transition: "height 0.15s ease-out",
          }}
        >
          <Divider />
          <Box
            sx={{
              p: 2,
              bgcolor: (theme) => theme.palette.grey[50],
            }}
          >
            {state.expandedGroups.has(group.productId) && (
              <VariantTable variants={group.variants} />
            )}
          </Box>
        </Box>
      </Paper>
    );
  };

  return (
    <Container maxWidth={false}>
      <PageHeader title={t("inventory.title")} icon={<InventoryIcon />} />

      <div className="w-full space-y-6">
        {/* Filters Section - Mobile Responsive */}
        <AdminFilterPanel>
          <Grid container spacing={3}>
            <Grid item xs={12} md>
              <Box sx={{ width: "100%" }}>
                <MuiAutocompleteMultiSelect
                  label={t("common.customer")}
                  options={state.data.customers}
                  value={state.selection.selectedCustomer ? [state.selection.selectedCustomer] : []}
                  onChange={(next) => {
                    const selected = next[0];
                    if (!selected) {
                      void handleCustomerSelect(null);
                      return;
                    }

                    void handleCustomerSelect(selected);
                  }}
                  getOptionLabel={(option) => option.name}
                  getOptionKey={(option) => option.id}
                  disabled={state.loading.isLoadingInitial || state.loading.isLoadingInventory}
                  loading={state.loading.isLoadingInitial}
                  sx={{
                    "& .MuiInputBase-root": {
                      height: "56px",
                    },
                  }}
                />
              </Box>
            </Grid>

            <Grid item xs={12} md>
              <MuiAutocompleteMultiSelect
                label={t("common.store")}
                options={state.data.stores}
                value={state.selection.selectedStore ? [state.selection.selectedStore] : []}
                onChange={(next) => handleStoreSearch(null, next[0] ?? null)}
                getOptionLabel={(option) => formatStoreSelectorLabel(option, t, user?.role, "name")}
                getOptionKey={(option) => option.id}
                disabled={
                  !state.selection.selectedCustomer ||
                  state.loading.isLoadingStores ||
                  state.loading.isLoadingInventory
                }
                loading={state.loading.isLoadingStores}
                sx={{
                  "& .MuiInputBase-root": {
                    height: "56px",
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} md="auto" sx={{ alignSelf: "center" }}>
              <Button
                variant="outlined"
                startIcon={<ClearIcon />}
                onClick={handleClearFilters}
                disabled={
                  (!state.selection.selectedCustomer && !state.selection.selectedStore) ||
                  state.loading.isLoadingInventory
                }
                color="primary"
                size="medium"
                sx={{ width: { xs: "100%", md: "auto" } }}
              >
                {t("common.clearFilters")}
              </Button>
            </Grid>
          </Grid>
        </AdminFilterPanel>

        {/* Stats Section - Show skeleton when loading */}
        {state.selection.selectedStore && (
          <Card sx={{ mb: 4 }}>
            <Box
              sx={{
                p: { xs: 2, sm: 3 },
                background: (theme) =>
                  `/* @noflip */ linear-gradient(45deg, ${theme.palette.primary.main}15, ${theme.palette.primary.light}15)`,
              }}
            >
              <Typography
                variant="h6"
                gutterBottom
                sx={{
                  color: "primary.main",
                  fontWeight: 600,
                  mb: 3,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <AnalyticsIcon /> {t("inventory.sections.healthOverview")}
              </Typography>
              <Grid container spacing={3}>
                {[
                  {
                    icon: <CategoryIcon />,
                    label: t("inventory.fields.uniqueProducts"),
                    value: state.loading.isLoadingInventory
                      ? "-"
                      : state.data.stats.uniqueProducts.toLocaleString(),
                    color: "primary.main",
                  },
                  {
                    icon: <InventoryIcon />,
                    label: t("inventory.totalVariants"),
                    value: state.loading.isLoadingInventory
                      ? "-"
                      : state.data.stats.totalVariants.toLocaleString(),
                    color: "secondary.main",
                  },
                  {
                    icon: <BasketIcon />,
                    label: t("inventory.fields.totalQuantity"),
                    value: state.loading.isLoadingInventory
                      ? "-"
                      : state.data.stats.totalQuantity.toLocaleString(),
                    color: "success.main",
                  },
                  {
                    icon: <AnalyticsIcon />,
                    label: t("inventory.fields.averageVariantsPerProduct"),
                    value: state.loading.isLoadingInventory
                      ? "-"
                      : state.data.stats.uniqueProducts > 0
                        ? (
                            state.data.stats.totalVariants / state.data.stats.uniqueProducts
                          ).toFixed(1)
                        : "0",
                    color: "warning.main",
                  },
                ].map((stat, index) => (
                  <Grid item xs={12} sm={6} md={3} key={index}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2.5,
                        height: "100%",
                        background: "white",
                        borderRadius: 2,
                        transition: "transform 0.2s",
                        "&:hover": {
                          transform: "translateY(-4px)",
                          boxShadow: 1,
                        },
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "flex-start", mb: 1 }}>
                        <Box
                          sx={{
                            p: 1,
                            borderRadius: 1,
                            bgcolor: stat.color,
                            color: "white",
                            mr: 2,
                          }}
                        >
                          {stat.icon}
                        </Box>
                        <Box>
                          <Typography variant="h4" sx={{ fontWeight: 600, mb: 0.5 }}>
                            {stat.value}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {stat.label}
                          </Typography>
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Card>
        )}

        {/* Content Section */}
        {!state.selection.selectedStore ? (
          <Card sx={{ p: 4, textAlign: "center" }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {!state.selection.selectedCustomer
                ? t("inventory.selectCustomerPrompt")
                : t("inventory.selectStorePrompt")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {!state.selection.selectedCustomer
                ? t("inventory.selectCustomerDescription")
                : t("inventory.selectStoreDescription")}
            </Typography>
          </Card>
        ) : state.loading.isLoadingInventory ? (
          <Card sx={{ p: 3 }}>
            <Box sx={{ mb: 4 }}>
              <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 2 }}>
                <CircularProgress size={24} />
                <Typography variant="h6" color="primary">
                  {t("inventory.loading.processingInventoryData")}
                </Typography>
              </Box>
              <Box sx={{ width: "100%", mb: 1 }}>
                <LinearProgress
                  variant="indeterminate"
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: "grey.100",
                    "& .MuiLinearProgress-bar": {
                      backgroundColor: "primary.main",
                    },
                  }}
                />
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {t("inventory.loading.storeInventoryMayTakeMoment")}
              </Typography>
            </Box>
            <Stack spacing={2}>
              {[...Array(5)].map((_, index) => (
                <Paper
                  key={index}
                  elevation={0}
                  sx={{
                    border: 1,
                    borderColor: "divider",
                    p: 2,
                    background: (theme) => theme.palette.grey[50],
                    position: "relative",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                    {/* Thumbnail skeleton */}
                    <Box
                      sx={{
                        width: 60,
                        height: 60,
                        bgcolor: "grey.200",
                        borderRadius: 1,
                        flexShrink: 0,
                        animation: "pulse 1.5s ease-in-out infinite",
                        "@keyframes pulse": {
                          "0%, 100%": { opacity: 1 },
                          "50%": { opacity: 0.5 },
                        },
                      }}
                    />

                    {/* Content skeleton */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box
                        sx={{
                          height: 20,
                          width: "40%",
                          bgcolor: "grey.200",
                          borderRadius: 0.75,
                          mb: 1,
                          animation: "pulse 1.5s ease-in-out infinite",
                        }}
                      />
                      <Box
                        sx={{
                          height: 16,
                          width: "60%",
                          bgcolor: "grey.200",
                          borderRadius: 0.75,
                          animation: "pulse 1.5s ease-in-out infinite",
                          animationDelay: "0.1s",
                        }}
                      />
                    </Box>

                    {/* Right side elements skeleton */}
                    <Stack direction="row" spacing={2} alignItems="center" sx={{ flexShrink: 0 }}>
                      {/* Variant chip skeleton */}
                      <Box
                        sx={{
                          height: 24,
                          width: 90,
                          bgcolor: "grey.200",
                          borderRadius: 3,
                          animation: "pulse 1.5s ease-in-out infinite",
                          animationDelay: "0.2s",
                        }}
                      />

                      {/* Stock chip skeleton */}
                      <Box
                        sx={{
                          height: 24,
                          width: 100,
                          bgcolor: "grey.200",
                          borderRadius: 3,
                          animation: "pulse 1.5s ease-in-out infinite",
                          animationDelay: "0.3s",
                        }}
                      />

                      {/* Price skeleton */}
                      <Box
                        sx={{
                          height: 24,
                          width: 70,
                          bgcolor: "grey.200",
                          borderRadius: 0.75,
                          animation: "pulse 1.5s ease-in-out infinite",
                          animationDelay: "0.4s",
                        }}
                      />

                      {/* Expand button skeleton */}
                      <Box
                        sx={{
                          height: 40,
                          width: 40,
                          bgcolor: "grey.200",
                          borderRadius: 1,
                          animation: "pulse 1.5s ease-in-out infinite",
                          animationDelay: "0.5s",
                        }}
                      />
                    </Stack>
                  </Box>
                </Paper>
              ))}
            </Stack>
          </Card>
        ) : state.data.displayInventory.length === 0 ? (
          <Card sx={{ p: 4, textAlign: "center" }}>
            <Typography variant="h6" color="text.secondary">
              {t("common.noDataAvailable")}
            </Typography>
          </Card>
        ) : (
          <Box sx={{ p: { xs: 2, sm: 3 } }}>
            <Box sx={{ mb: 3 }}>
              {/* Search Bar */}
              <Box sx={{ mb: 3 }}>
                <TextField
                  fullWidth
                  placeholder={t("inventory.placeholders.searchInventory")}
                  value={state.filters.searchQuery}
                  onChange={handleSearch}
                  InputProps={{
                    startAdornment: (
                      <Box component="span" sx={{ color: "text.secondary", mr: 1 }}>
                        🔍
                      </Box>
                    ),
                    endAdornment: state.filters.searchQuery ? (
                      <IconButton
                        size="small"
                        onClick={() =>
                          handleSearch({
                            target: { value: "" },
                          } as React.ChangeEvent<HTMLInputElement>)
                        }
                        sx={{ mr: -1 }}
                      >
                        <ClearIcon />
                      </IconButton>
                    ) : null,
                    sx: {
                      bgcolor: "background.paper",
                      "&:hover": {
                        bgcolor: "background.paper",
                      },
                    },
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "divider",
                      },
                      "&:hover fieldset": {
                        borderColor: "primary.main",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "primary.main",
                      },
                    },
                  }}
                />
              </Box>

              {/* Results count */}
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography variant="subtitle1">
                  {state.filters.searchQuery ? (
                    <>
                      {t("inventory.search.results", {
                        count: state.data.filteredInventory.length,
                      })}
                      {state.data.filteredInventory.length < state.data.displayInventory.length && (
                        <Typography component="span" color="text.secondary">
                          {" "}
                          {t("inventory.search.filteredTotal", {
                            count: state.data.displayInventory.length,
                          })}
                        </Typography>
                      )}
                    </>
                  ) : (
                    t("common.showingItems", { count: state.data.displayInventory.length })
                  )}
                </Typography>
                {state.filters.searchQuery && state.data.filteredInventory.length === 0 && (
                  <Button
                    startIcon={<ClearIcon />}
                    onClick={() =>
                      handleSearch({ target: { value: "" } } as React.ChangeEvent<HTMLInputElement>)
                    }
                  >
                    {t("inventory.actions.clearSearch")}
                  </Button>
                )}
              </Box>

              {state.error && (
                <Typography color="error" sx={{ mt: 1 }}>
                  {state.error}
                </Typography>
              )}
            </Box>

            {state.filters.searchQuery && state.data.filteredInventory.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 8 }}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  {t("inventory.search.noMatchesFound")}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t("inventory.search.adjustSearchTerms")}
                </Typography>
              </Box>
            ) : (
              <Stack spacing={1}>
                {(state.filters.searchQuery
                  ? state.data.filteredInventory
                  : state.data.displayInventory
                ).map((group) => (
                  <React.Fragment key={group.productId}>{renderProductItem(group)}</React.Fragment>
                ))}
              </Stack>
            )}
          </Box>
        )}
      </div>
    </Container>
  );
};

// Create a mobile variant table component
const MobileVariantTable = React.memo(
  ({ variants }: { variants: GroupedInventoryItem["variants"] }) => {
    const { t } = useTranslation();

    return (
      <List
        sx={{
          width: "100%",
          bgcolor: "background.paper",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
          "& .MuiListItem-root": {
            flexDirection: "column",
            alignItems: "flex-start",
            gap: 1,
            py: 2,
          },
        }}
      >
        {variants.map((variant, index) => (
          <React.Fragment key={`${variant.skuId}-${variant.color}-${variant.size}-${index}`}>
            {index > 0 && <Divider />}
            <ListItem>
              <Box sx={{ width: "100%" }}>
                {/* SKU and Color */}
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ mb: 1 }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: "monospace",
                      fontWeight: 500,
                      color: "text.secondary",
                    }}
                  >
                    {variant.skuId}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box
                      sx={{
                        width: 16,
                        height: 16,
                        borderRadius: "50%",
                        bgcolor: variant.color.toLowerCase(),
                        border: "1px solid",
                        borderColor: "divider",
                      }}
                    />
                    <Typography variant="body2">{variant.color}</Typography>
                  </Box>
                </Stack>

                {/* Size and Quantity */}
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {t("inventory.variant.sizeLabel", {
                        size: variant.size,
                        secondarySize: variant.size2 ? ` / ${variant.size2}` : "",
                      })}
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {t("inventory.variant.quantityLabel", { quantity: variant.quantity })}
                    </Typography>
                    <Chip
                      label={
                        variant.quantity > 0 ? t("inventory.inStock") : t("inventory.outOfStock")
                      }
                      size="small"
                      color={variant.quantity > 0 ? "success" : "error"}
                      variant="outlined"
                      sx={{
                        height: 24,
                        fontSize: "0.75rem",
                        fontWeight: 500,
                      }}
                    />
                  </Stack>
                </Stack>
              </Box>
            </ListItem>
          </React.Fragment>
        ))}
      </List>
    );
  },
);
MobileVariantTable.displayName = "MobileVariantTable";

export default Inventory;
